// modules/BlueprintSystem.js
// 装备图纸系统模块
import DebugHelper from './DebugHelper.js';

export default class BlueprintSystem {
    /**
     * 检查玩家是否拥有指定装备的图纸
     * @param {Object} player - 玩家对象
     * @param {string} equipmentId - 装备ID
     * @returns {boolean} 是否拥有图纸
     */
    static hasBlueprint(player, equipmentId) {
        if (!player || !player.inventory) {
            return false;
        }
        
        const blueprintId = `blueprint_${equipmentId}`;
        
        return player.inventory.some(item => {
            return item.type === 'blueprint' && 
                   (item.id === blueprintId || item.equipmentId === equipmentId);
        });
    }
    
    /**
     * 获取玩家拥有的所有图纸
     * @param {Object} player - 玩家对象
     * @returns {Array<Object>} 图纸列表
     */
    static getPlayerBlueprints(player) {
        if (!player || !player.inventory) {
            return [];
        }
        
        return player.inventory.filter(item => item.type === 'blueprint');
    }
    
    /**
     * 获取可合成的装备列表（玩家有图纸的装备）
     * @param {Object} player - 玩家对象
     * @param {Object} equipmentData - 装备数据（从JSON加载）
     * @returns {Array<Object>} 可合成的装备列表
     */
    static getCraftableEquipments(player, equipmentData) {
        if (!player || !equipmentData || !equipmentData.vehicleEquipment) {
            return [];
        }
        
        const blueprints = this.getPlayerBlueprints(player);
        const craftableEquipments = [];
        
        equipmentData.vehicleEquipment.forEach(equipment => {
            const hasBlueprint = blueprints.some(bp => {
                const blueprintEquipmentId = bp.equipmentId || bp.id?.replace('blueprint_', '');
                return blueprintEquipmentId === equipment.id;
            });
            
            if (hasBlueprint) {
                craftableEquipments.push(equipment);
            }
        });
        
        return craftableEquipments;
    }
    
    /**
     * 创建装备图纸
     * @param {string} equipmentId - 装备ID
     * @param {string} equipmentName - 装备名称
     * @returns {Object} 图纸对象
     */
    static createBlueprint(equipmentId, equipmentName) {
        return {
            id: `blueprint_${equipmentId}`,
            name: `${equipmentName}图纸`,
            type: 'blueprint',
            equipmentId: equipmentId,
            equipmentName: equipmentName,
            description: `用于合成 ${equipmentName} 的图纸`,
            price: 0 // 图纸不可出售，或设置一个价格
        };
    }
    
    /**
     * 添加图纸到玩家背包
     * @param {Object} player - 玩家对象
     * @param {string} equipmentId - 装备ID
     * @param {string} equipmentName - 装备名称
     * @returns {boolean} 是否添加成功
     */
    static addBlueprint(player, equipmentId, equipmentName) {
        if (!player || !player.inventory) {
            return false;
        }
        
        // 检查是否已有该图纸
        if (this.hasBlueprint(player, equipmentId)) {
            DebugHelper.log(`玩家已拥有 ${equipmentName}图纸`);
            return false;
        }
        
        const blueprint = this.createBlueprint(equipmentId, equipmentName);
        player.inventory.push(blueprint);
        
        DebugHelper.log(`添加图纸: ${blueprint.name}`);
        return true;
    }
    
    /**
     * 获取所有可购买的图纸列表
     * @param {Object} equipmentData - 装备数据
     * @returns {Array<Object>} 图纸列表（用于商店）
     */
    static getAllBlueprints(equipmentData) {
        if (!equipmentData || !equipmentData.vehicleEquipment) {
            return [];
        }
        
        return equipmentData.vehicleEquipment.map(equipment => {
            return {
                id: `blueprint_${equipment.id}`,
                name: `${equipment.name}图纸`,
                type: 'blueprint',
                equipmentId: equipment.id,
                equipmentName: equipment.name,
                description: `用于合成 ${equipment.name} 的图纸`,
                price: this.calculateBlueprintPrice(equipment.tier || 1),
                tier: equipment.tier || 1
            };
        });
    }
    
    /**
     * 计算图纸价格（根据装备等级）
     * @param {number} tier - 装备等级
     * @returns {number} 图纸价格
     */
    static calculateBlueprintPrice(tier = 1) {
        // 根据等级定价
        const basePrice = 500;
        return basePrice * tier;
    }
}

